import React, { useState, useEffect } from "react";
import { useSelector, useDispatch } from "react-redux";

import "bootstrap/dist/css/bootstrap.min.css";
import "./style.css";
import {
  fetchTags,
  deleteTags,
  editTags,
  toggleTagSelection,
  toggleAllSelection,
  createTag,
} from "../../store/Blog/tagsSlice";
import Swal from "sweetalert2";

const TagsManagement = () => {
  const dispatch = useDispatch();
  const { tags, loading, error } = useSelector((state) => state.tags);

  // State for form inputs
  const [tagName, setTagName] = useState("");
  const [tagSlug, setTagSlug] = useState("");
  const [tagDescription, setTagDescription] = useState("");
  const [searchTerm, setSearchTerm] = useState("");
  const [bulkAction, setBulkAction] = useState("-- Select --");

  // Fetch tags on component mount
  useEffect(() => {
    dispatch(fetchTags());
  }, [dispatch]);

  // Handle adding a new tag
  const handleAddTag = () => {
    if (!tagName.trim()) {
      alert("Tag name is required");
      return;
    }

    const newTag = {
      name: tagName,
      description: tagDescription,
      slug: tagSlug || tagName.toLowerCase().replace(/\s+/g, "-"),
      count: 0,
      selected: false,
    };

    dispatch(createTag(newTag))
      .unwrap()
      .then(() => {
        setTagName("");
        setTagSlug("");
        setTagDescription("");
      });
  };

  // Handle search
  const handleSearch = () => {
    // In a real app, this would filter the tags list
    console.log("Searching for:", searchTerm);
  };

  // Handle bulk actions
  const handleBulkAction = async () => {
  if (bulkAction === "-- Select --") {
    Swal.fire({
      icon: 'warning',
      title: 'Action Required',
      text: 'Please select a bulk action',
    });
    return;
  }

  const selectedTags = tags.filter((tag) => tag.selected);
  if (selectedTags.length === 0) {
    Swal.fire({
      icon: 'warning',
      title: 'No Selection',
      text: 'No tags selected',
    });
    return;
  }

  const selectedIds = selectedTags.map((tag) => tag._id);

  if (bulkAction === "Delete") {
    const result = await Swal.fire({
      title: 'Are you sure?',
      text: `You are about to delete ${selectedTags.length} tag(s). This action cannot be undone!`,
      icon: 'warning',
      showCancelButton: true,
      confirmButtonColor: '#3085d6',
      cancelButtonColor: '#d33',
      confirmButtonText: 'Yes, delete them!'
    });

    if (result.isConfirmed) {
      try {
        await dispatch(deleteTags(selectedIds));
        Swal.fire(
          'Deleted!',
          `Your ${selectedTags.length} tag(s) have been deleted.`,
          'success'
        );
      } catch (error) {
        Swal.fire(
          'Error!',
          'Failed to delete tags. Please try again.',
          'error'
        );
      }
    }
  } else if (bulkAction === "Edit") {
    const { value: newName } = await Swal.fire({
      title: 'Edit Tags',
      input: 'text',
      inputLabel: 'Enter new name for selected tags',
      inputPlaceholder: 'New tag name',
      showCancelButton: true,
      inputValidator: (value) => {
        if (!value) {
          return 'You need to enter a name!';
        }
      }
    });

    if (newName) {
      try {
        await dispatch(editTags({ ids: selectedIds, name: newName }));
        Swal.fire(
          'Updated!',
          `Your ${selectedTags.length} tag(s) have been updated.`,
          'success'
        );
      } catch (error) {
        Swal.fire(
          'Error!',
          'Failed to update tags. Please try again.',
          'error'
        );
      }
    }
  }
};

  // Toggle tag selection
  const handleToggleTagSelection = (id) => {
    dispatch(toggleTagSelection(id));
  };

  // Toggle all tags selection
  const handleToggleAllTagsSelection = (e) => {
    dispatch(toggleAllSelection(e.target.checked));
  };

  if (loading) return <div>Loading tags...</div>;
  if (error) return <div>Error: {error}</div>;

  return (
    <div
      className="product-wrap col-md-10 mx-auto mt-3 p-3"
      style={{ background: "#F6F6F6" }}
    >
      <div className="Container">
        <div className="row mb-3">
          <div className="col-md-6">
            <h2>Tag</h2>
          </div>
          <div className="col-md-6">
            <div className="header-rgt">
              <input
                type="text"
                className="form-control wdt-40"
                placeholder="Search tags..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
              />
              <button className="btn-cls" onClick={handleSearch}>
                Search Tags
              </button>
            </div>
          </div>
        </div>

        <div className="row">
          <div className="col-md-4 add-category">
            <h4>Add New Tag</h4>

            <div className="form-group mb-3">
              <label>Name</label>
              <input
                type="text"
                className="form-control"
                value={tagName}
                onChange={(e) => setTagName(e.target.value)}
                placeholder="Tag name"
              />
              <small className="form-text text-muted">
                The name is how it appears on your site.
              </small>
            </div>

            <div className="form-group mb-3">
              <label>Slug</label>
              <input
                type="text"
                className="form-control"
                value={tagSlug}
                onChange={(e) => setTagSlug(e.target.value)}
                placeholder="Tag slug (optional)"
              />
              <small className="form-text text-muted">
                The "slug" is the URL-friendly version of the name. It is
                usually all lowercase and contains only letters, numbers, and
                hyphens.
              </small>
            </div>

            <div className="form-group mb-3">
              <label>Description</label>
              <input
                type="text"
                className="form-control"
                value={tagDescription}
                onChange={(e) => setTagDescription(e.target.value)}
                placeholder="Description (optional)"
              />
              <small className="form-text text-muted custom-txt">
                The description is not prominent by default; however, some
                themes may show it.
              </small>
            </div>

            <button className="btn-cls" onClick={handleAddTag}>
              Add New Tag
            </button>
          </div>

          <div className="col-md-1"></div>

          <div className="col-md-7">
            <div className="blog-inner tab-brdr">
              <div className="table-header">
                <div className="filter">
                  <div className="w-100">
                    <div className="row">
                      <div className="col-md-8">
                        <div className="frm-outer">
                          <select
                            className="form-control form-select"
                            value={bulkAction}
                            onChange={(e) => setBulkAction(e.target.value)}
                          >
                            <option value="-- Select --">Bulk Action</option>
                            <option value="Delete">Delete</option>
                            <option value="Edit">Edit</option>
                          </select>

                          <button
                            className="btn-cls"
                            onClick={handleBulkAction}
                          >
                            Apply
                          </button>
                        </div>
                      </div>

                      <div className="col-md-4 right-align">
                        <p style={{ textAlign: "right" }}>
                          {tags.length} Items
                        </p>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              <table className="table table-striped m-0">
                <thead>
                  <tr>
                    <th>
                      <input
                        type="checkbox"
                        className="form-check-input"
                        onChange={handleToggleAllTagsSelection}
                        checked={
                          tags.length > 0 && tags.every((tag) => tag.selected)
                        }
                      />
                    </th>
                    <th>Name</th>
                    <th>Description</th>
                    <th>Slug</th>
                    <th>Count</th>
                  </tr>
                </thead>
                <tbody>
                  {tags.map((tag) => (
                    <tr key={tag.id}>
                      <td>
                        <input
                          type="checkbox"
                          className="form-check-input"
                          checked={tag.selected}
                          onChange={() => handleToggleTagSelection(tag.id)}
                        />
                      </td>
                      <td>{tag.name}</td>
                      <td>{tag.description}</td>
                      <td>{tag.slug}</td>
                      <td>{tag.count}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default TagsManagement;
